import _ from 'lodash'
import Fuse from 'fuse.js'
import { LitElement, html, type PropertyValues, nothing } from 'lit'
import { customElement, property, state, query } from 'lit/decorators.js'
import type { SupportedLanguage } from '../i18n/i18n.no-deps'
import type { TagList } from './zaptic-tag-list'
import { timeout } from '../lib/timeout.no-deps'
import './zaptic-deleted-assignee'
import './zaptic-assignable-icon'
import style from './style/zaptic-tag-select-style'
import './zaptic-icon'
import './zaptic-button'
import './zaptic-popover'
import './zaptic-delete-icon'
import type { Tag } from '../domain/tags/types.no-deps'

export enum Events {
    onChange = 'onChange',
}

export interface CustomEvents {
    [Events.onChange]: CustomEvent<Tag[]>
}

/**
 *
 * If you need something like this but a bit more generic have a look at SearchableItemList.tsx
 */
@customElement('zaptic-tag-select')
export class TagSelect extends LitElement {
    static styles = style

    @property()
    placeholder: string

    @property({ type: Array })
    tags: Tag[] = []

    @property({ type: Array })
    selected: Tag[] = []

    @property()
    language: SupportedLanguage

    @property({ type: Boolean })
    disabled = false

    @property({ type: Boolean })
    'multi-select' = false

    @property({ type: Boolean })
    displaySelectedTagInSearchInput: boolean = true

    @property()
    'no-data-message': string | null = null

    @property({ type: Boolean })
    'search-dropdown' = true

    @property({ type: Boolean })
    'show-clear-all-button' = true

    @property({ type: Boolean, attribute: 'limit-height' })
    limitHeight = false

    @state()
    _selectedTags = new Array<Tag>()

    @state()
    _filter: string = ''

    @state()
    _focused: boolean = false

    @state()
    _selectedIndex: number = 0

    @state()
    _filteredTags: Tag[]

    @query('zaptic-tag-list')
    _tagList: TagList

    @query('.main')
    _inputMain: HTMLElement

    willUpdate(changedProperties: PropertyValues<TagSelect>) {
        if (changedProperties.has('_filter')) {
            this._selectedIndex = 0
            this._filteredTags = this.getFilteredTags()
        }
        if (changedProperties.has('selected') || changedProperties.has('tags')) {
            this._selectedTags = this.selected
        }

        if (changedProperties.has('tags')) {
            this._filter = ''
            this._filteredTags = this.getFilteredTags()
        }
    }

    updated(changedProperties: PropertyValues<TagSelect>) {
        if (changedProperties.has('_filteredTags')) {
            this.scrollSelectedIntoView()
        }
    }

    private fuzzyFilter(tags: Tag[], search: string) {
        if (search === '') {
            return tags
        }
        const fuse = new Fuse(tags, { keys: ['name'], threshold: 0.3 })
        return fuse.search(search).map((result) => result.item)
    }

    private scrollSelectedIntoView() {
        if (this._tagList != null) {
            this._tagList.scrollSelectedIntoView()
        }
    }

    private getFilteredTags() {
        const tags = this.tags

        const cleanTags = tags.filter((item) => item.name !== '')
        return !this._filter.trim() ? cleanTags : this.fuzzyFilter(cleanTags, this._filter)
    }

    private tagSelectedAlready(tag: Tag) {
        return _.some(this._selectedTags, tag)
    }

    private removeTag(tagToRemove: Tag) {
        this.setTags(this._selectedTags.filter(({ name }) => name !== tagToRemove.name))
    }

    private addTag(profile: Tag) {
        this.setTags(this._selectedTags.concat(profile))
    }

    private focusHandler(value: boolean) {
        return {
            handleEvent: async (evt: FocusEvent) => {
                // Prevent bluring if we are going from one child to another
                if (this.renderRoot.contains(evt.relatedTarget as Node) && evt.type === 'blur') {
                    return
                }

                // Add wait to avoid race condition between 2 html renders (1 from here and one from handle select)
                await timeout(100)

                this._focused = value
            },
            capture: true,
        }
    }

    private handleHover({ detail }: { detail: number }) {
        this._selectedIndex = detail
    }

    private handleSelect({ detail }: { detail: number }) {
        const item: Tag = this._filteredTags[detail]

        if (this.tagSelectedAlready(item)) {
            this.removeTag(item)
        } else {
            this.addTag(item)
        }

        // If in multi-select mode, keep dropdown list open
        this._focused = this['multi-select']

        // focus on search input after selection when in multi-select mode
        if (this['multi-select']) {
            this._inputMain.focus()
        }
    }

    private handleDeselect(setFilter = '', deselectedTag: Tag) {
        // Delete de-selected tag from selected tag array
        this.removeTag(deselectedTag)

        this._filter = setFilter

        // focus on search input
        this._inputMain.focus()
    }

    private handleFilter(evt: any) {
        this._filter = evt.target.value
    }

    private handleKeyDown(event: KeyboardEvent) {
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault()
                this._selectedIndex = Math.min(this._selectedIndex + 1, this._filteredTags.length - 1)
                break
            case 'ArrowUp':
                event.preventDefault()
                this._selectedIndex = Math.max(this._selectedIndex - 1, 0)
                break
            case 'Tab':
            case 'Enter':
                event.preventDefault()
                this.handleSelect({ detail: this._selectedIndex })
                return
            default:
                return
        }

        this.scrollSelectedIntoView()
    }

    private tagLabel(selectedTag: Tag, _disableMultiselect: boolean) {
        return html`
            <div class="selected-tag" tabindex="0">
                <span class="selected-tag-name">${selectedTag?.name}</span>
            </div>
        `
    }

    private displayRemovableTag(selectedTag: Tag, disableMultiselect: boolean) {
        const label = this.tagLabel(selectedTag, disableMultiselect)
        return html`
            <div class="selected" style=${disableMultiselect ? 'cursor: default;' : 'cursor: pointer;'}>
                ${label}${disableMultiselect ? '' : this.deleteIcon(selectedTag)}
            </div>
        `
    }

    private deleteIcon(selectedTag: Tag) {
        return html`<zaptic-delete-icon
            @click=${() => this.handleDeselect('', selectedTag)}
            @keyup=${(evt: KeyboardEvent) => evt.key === 'Enter' && this.handleDeselect('', selectedTag)}
        ></zaptic-delete-icon>`
    }

    private searchInput() {
        const placeholder = this._selectedTags.length === 0 ? this.placeholder : ''
        const value = this._selectedTags.length === 0 || this._focused ? this._filter : ''

        return html`<input
            class="main"
            data-testid="tag-select-input"
            @input=${this.handleFilter}
            .value=${value}
            placeholder=${placeholder}
            @keydown=${this.handleKeyDown}
            ?disabled=${this.disabled}
            style=${this.disabled ? 'visibility: hidden;' : ''}
        />`
    }

    private async unselectAllTags() {
        this.setTags([])

        // Add some wait to avoid race condition between 2 renders
        await timeout(100)
        this._inputMain.focus()
    }

    private displaySingleTag(selectedTag: Tag) {
        return html` ${html`<div class="selected-single-tag">${selectedTag.name}</div>`}`
    }

    private tagSearchInput() {
        // If either no tag is selected in single select mode
        const noTagSelectedInSingleMode = !this['multi-select'] && !this._selectedTags.length

        // Multiselect mode and is disabled
        const disableMultiselect = this['multi-select'] && this.disabled

        const shouldRenderChevronIcon = !disableMultiselect && this['search-dropdown']

        // Multiselect mode and is not disabled
        const shouldTakeMultiselectInput = this['multi-select'] && !this.disabled

        const shouldTakeInput = !this['multi-select']
            ? noTagSelectedInSingleMode
            : !disableMultiselect || shouldTakeMultiselectInput

        return html`<div class="tag-input-wrapper" tabindex=${shouldTakeInput ? '-1' : ''}>
            <div class="tag-input ${this.limitHeight ? 'tag-input--limited-height' : ''}">
                ${!this['search-dropdown']
                    ? html`<div class="magnifier-icon"><zaptic-icon icon-name="magnifier"></zaptic-icon></div>`
                    : nothing}
                ${this._selectedTags?.length && this.displaySelectedTagInSearchInput
                    ? this._selectedTags.map((selectedTag) => {
                          return this['multi-select']
                              ? this.displayRemovableTag(selectedTag, disableMultiselect)
                              : this.displaySingleTag(selectedTag)
                      })
                    : nothing}
                ${noTagSelectedInSingleMode || this['multi-select'] ? this.searchInput() : ''}
            </div>
            ${!this.displaySelectedTagInSearchInput && this._filter.length
                ? html`<zaptic-delete-icon
                      @click=${this.clearSearchInputValue}
                      class="delete-all-icon"
                  ></zaptic-delete-icon>`
                : ''}
            ${this.displaySelectedTagInSearchInput && this._selectedTags.length && !disableMultiselect
                ? html`<zaptic-delete-icon
                      @click=${this.unselectAllTags}
                      class="delete-all-icon"
                      data-testid="clear-all-tag-select"
                  ></zaptic-delete-icon>`
                : ''}
            ${!shouldRenderChevronIcon
                ? ''
                : html`<svg
                      class=${this._focused ? 'chevron-up' : ''}
                      width="12"
                      height="7"
                      viewBox="0 0 12 7"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                  >
                      <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M11.7365 0.225291C12.0458 0.530861 12.0458 1.03024 11.7365 1.33581L6.90974 6.10455C6.39859 6.60955 5.56987 6.60955 5.05873 6.10455L0.231966 1.33581C-0.0773221 1.03024 -0.077322 0.530861 0.231966 0.225291C0.536009 -0.0750969 1.0251 -0.0750972 1.32914 0.225291L5.98423 4.82442L10.6393 0.225291C10.9434 -0.075097 11.4325 -0.0750967 11.7365 0.225291Z"
                          fill="#7E8B9E"
                      />
                  </svg>`}
        </div>`
    }

    private clearSearchInputValue() {
        this._filter = ''
    }

    private clearAllButtonRow() {
        return html`
            <div class="footer footer--padded">
                <zaptic-button
                    size="small"
                    type="secondary"
                    @click="${this.unselectAllTags}"
                    disabled="${!this._selectedTags.length}"
                    >${window.i18n('shared:common.clearAll')}</zaptic-button
                >
            </div>
        `
    }

    private tagOptions() {
        return html`
            <zaptic-tag-list
                .tags=${this._filteredTags}
                .selected=${this._selectedIndex}
                .language=${this.language}
                .selected-tags=${this._selectedTags}
                @onHovered=${this.handleHover}
                @onSelected=${this.handleSelect}
            ></zaptic-tag-list>
            ${!this['search-dropdown'] && this['show-clear-all-button'] ? this.clearAllButtonRow() : ''}
        `
    }

    private setTags(updatedTags: Tag[]) {
        this.dispatchEvent(new CustomEvent(Events.onChange, { detail: updatedTags }))
    }

    private hasTagToDisplay() {
        return this.tags.length
    }

    private displayNoDataMessage() {
        return html` <zaptic-popover style=${this._focused ? '' : 'display: none;'}>
            ${html`<div class="display-no-data">
                <div class="display-no-data-text">${this['no-data-message']}</div>
            </div>`}
        </zaptic-popover>`
    }

    /**
     * Renders the component with a search input that displays the rest of the component elements in a drop-down area.
     */
    private renderDropdownLayout() {
        const dropdownContent = () => html`
            <zaptic-popover style=${this._focused ? '' : 'display: none;'}> ${this.tagOptions()} </zaptic-popover>
        `

        return html`
            <div class="root" @focus=${this.focusHandler(true)} @blur=${this.focusHandler(false)}>
                ${this.tagSearchInput()} ${this.hasTagToDisplay() ? dropdownContent() : this.displayNoDataMessage()}
            </div>
        `
    }

    /**
     * Renders the component with all of its elements fully visible.
     */
    private renderFullLayout() {
        return html`<div class="root root--full-mode">
            <div>${this.tagSearchInput()} ${this.tagOptions()}</div>
        </div>`
    }

    render() {
        if (this['search-dropdown']) {
            return this.renderDropdownLayout()
        }

        return this.renderFullLayout()
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'zaptic-tag-select': TagSelect
    }
}
